import { useRouter, useLocalSearchParams, Href } from 'expo-router';
import { useSetAtom } from 'jotai';
import { AlertTriangleIcon } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';

import { SupportForm } from '@/components/supportForm';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Link, LinkText } from '@/components/ui/link';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { buildAuthPathWithRedirect } from '@/hooks/useRedirectPath';
import { fetchAuthSession, signInWithMagicLink, signOutLocal } from '@/services/auth';
import { redirectAfterConfirmationAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import { getMagicLinkErrorCopy, trackAuthCallbackError } from '@/util/analyticsEvents';
import { sanitizeRedirectDestination } from '@/util/redirectDestination';

type ErrorType = 'missing_params' | 'auth_failed' | 'expired_or_invalid';

/**
 * Magic Link callback screen (/auth/magic-link)
 *
 * Handles magic link authentication when users click a link from their email.
 *
 * FLOW:
 * 1. Extract token, email, and optional redirect from URL params
 * 2. Call signInWithMagicLink (CUSTOM_WITHOUT_SRP + confirmSignIn)
 * 3. Load user profile via loadCurrentUser
 * 4. Redirect to the redirect param or default destination
 *
 * UI STATES:
 * - Working: "Signing in..." with spinner
 * - Error: Warning box with sign-in and contact support links
 *
 * GUARDS:
 * - useRef prevents duplicate auth attempts (React Strict Mode)
 * - Cache-Control: no-store prevents browser caching
 */

export default function MagicLinkScreen() {
  const { replace } = useRouter();

  const { loadCurrentUser } = useAuth();

  const setRedirectAfterConfirmation = useSetAtom(redirectAfterConfirmationAtom);

  const params = useLocalSearchParams<{
    token?: string;
    email?: string;
    redirect?: string;
    redirectAfterConfirmation?: string;
  }>();

  const { token, email, redirect, redirectAfterConfirmation } = useMemo(
    () => ({
      token: (params.token as string) || '',
      email: (params.email as string) || '',
      redirect: (params.redirect as string) || '',
      redirectAfterConfirmation: (params.redirectAfterConfirmation as string) || '',
    }),
    [params]
  );

  const defaultRedirectPath = '/(secure)/(tabs)/directory';

  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle');

  const [errType, setErrType] = useState<ErrorType | undefined>(undefined);

  const hasAttemptedRef = useRef(false);

  const [showSupportModal, setShowSupportModal] = useState(false);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const meta = document.createElement('meta');

      meta.httpEquiv = 'Cache-Control';

      meta.content = 'no-store';

      document.head.appendChild(meta);
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      if (hasAttemptedRef.current) {
        return;
      }

      hasAttemptedRef.current = true;

      if (!token || !email) {
        setStatus('error');

        setErrType('missing_params');

        return;
      }

      setStatus('working');

      try {
        const session = await fetchAuthSession();

        if (session.tokens) {
          await signOutLocal();
        }
      } catch (err) {
        console.debug('No active session or sign-out failed — proceeding with sign-in', err);
      }

      try {
        const result = await signInWithMagicLink(email, token);

        if (!result.isSignedIn) {
          setStatus('error');

          setErrType('auth_failed');

          trackAuthCallbackError({
            method: 'magic_link',
            source: 'magic_link_callback',
            reason: 'challenge_not_verified',
          });

          return;
        }

        segmentClient.track(ANALYTICS_EVENTS.LOG_IN, {
          method: 'magic_link',
          source: 'magic_link_callback',
          email,
        });

        await loadCurrentUser();

        if (redirectAfterConfirmation) {
          setRedirectAfterConfirmation(redirectAfterConfirmation);
        }

        const destination = sanitizeRedirectDestination(redirect) ?? defaultRedirectPath;

        replace(destination as Href);
      } catch (error) {
        const isExpiredOrInvalid =
          error instanceof Error &&
          (error.message.includes('NotAuthorizedException') ||
            error.message.includes('not authorized') ||
            error.message.includes('Incorrect'));

        setStatus('error');

        setErrType(isExpiredOrInvalid ? 'expired_or_invalid' : 'auth_failed');

        trackAuthCallbackError({
          method: 'magic_link',
          source: 'magic_link_callback',
          reason: isExpiredOrInvalid ? 'expired_or_invalid_token' : 'auth_error',
          error,
          extra: { email, hasToken: !!token, hasRedirect: !!redirect },
        });
      }
    };

    run();
  }, [token, email, redirect, redirectAfterConfirmation, replace, setRedirectAfterConfirmation]);

  if (status === 'working') {
    return (
      <VStack className="flex-1 items-center justify-center gap-6 p-6">
        <Spinner size="large" />
        <Text className="text-center font-semibold text-xl">Signing in…</Text>
      </VStack>
    );
  }

  if (status === 'error') {
    const { title: errorBoxTitle, subtitle: errorBoxSubtitle } = getMagicLinkErrorCopy(errType);

    return (
      <VStack className="flex-1 items-center justify-center gap-6 p-6">
        <VStack className="w-full max-w-[420px] rounded-md bg-background-warning px-4 py-4">
          <HStack className="items-start gap-2">
            <Icon as={AlertTriangleIcon} className="mt-0.5 h-[18px] w-[18px] text-warning-800" />

            <VStack className="flex-1 gap-1.5">
              <Text size="sm" className="font-semibold text-typography-900">
                {errorBoxTitle}
              </Text>

              <Text size="xs" className="text-typography-700">
                {errorBoxSubtitle}
              </Text>

              <HStack className="mt-2 items-center">
                <Link
                  onPress={() =>
                    replace(buildAuthPathWithRedirect(ROUTES.LOGIN, redirect) as Href)
                  }>
                  <LinkText size="xs" className="text-warning-800 underline">
                    Sign in
                  </LinkText>
                </Link>
                <Text size="xs" className="mx-1 text-typography-700">
                  or
                </Text>
                <Link onPress={() => setShowSupportModal(true)}>
                  <LinkText size="xs" className="text-warning-800 underline">
                    Contact Support
                  </LinkText>
                </Link>
              </HStack>
            </VStack>
          </HStack>
        </VStack>

        {showSupportModal ? (
          <SupportForm
            isOpen={showSupportModal}
            onClose={() => setShowSupportModal(false)}
            initialValues={{ email }}
          />
        ) : null}
      </VStack>
    );
  }

  return null;
}
